import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import CheckIcon from '@mui/icons-material/Check';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { SHARE_LEVEL_OPTIONS } from '@components/Projects/shareLevels';
import {
  type ShareAccessLevel,
  useRemoveFolderShareMutation,
  useUpdateFolderShareMutation,
} from '@store/api/folderEndpoints';
import {
  memberUserId,
  useAddTeamMembersMutation,
  useDeleteTeamMutation,
  useGetTeamSharesQuery,
  useGetTeamsQuery,
  useRemoveTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useUpdateTeamMutation,
  type TeamMember,
  type TeamMemberInput,
  type TeamMemberUser,
  type TeamRole,
  type TeamShareEntry,
} from '@store/api/teamEndpoints';
import { useGetUsersQuery } from '@store/api/userEndpoints';
import { usePermission } from '@store/hooks';
import { useMemo, useState } from 'react';
import {
  AngleLeftIcon,
  EditIcon,
  SearchIcon,
  TimesIcon,
  TrashIcon,
  UserFriendsIcon,
  UserIcon,
  UserPlusIcon,
} from 'react-line-awesome';
import { useNavigate, useParams } from 'react-router-dom';
import {
  backButtonStyles,
  buttonStyles,
  candidateListStyles,
  candidateRowStyles,
  candidateSelectedStyles,
  checkCircleSelectedStyles,
  checkCircleStyles,
  countPillStyles,
  dangerHoverStyles,
  detailIconStyles,
  dialogCloseStyles,
  dialogFooterStyles,
  dialogHeaderStyles,
  dialogPaperStyles,
  memberRowStyles,
  menuItemStyles,
  roleChipStyles,
  selectStyles,
} from './Teams.styles';

const TEAM_ROLE_OPTIONS: { value: TeamRole; labelKey: string; descriptionKey: string }[] = [
  { value: 'MANAGER', labelKey: 'Team_Role_Manager', descriptionKey: 'Team_Role_Manager_Desc' },
  { value: 'INSPECTOR', labelKey: 'Team_Role_Inspector', descriptionKey: 'Team_Role_Inspector_Desc' },
  { value: 'ANALYST', labelKey: 'Team_Role_Analyst', descriptionKey: 'Team_Role_Analyst_Desc' },
];

const resolveMemberUser = (member: TeamMember): TeamMemberUser | undefined =>
  typeof member.userId === 'object' && member.userId !== null ? member.userId : undefined;

const MemberAvatar = ({ name, size = 40 }: { name: string; size?: number }) => (
  <Avatar
    aria-label={name}
    sx={{
      width: size,
      height: size,
      bgcolor: '#26272F',
      border: '1px solid rgb(54, 56, 67)',
      color: '#B5B7C8',
      fontSize: size * 0.5,
    }}
  >
    <UserIcon />
  </Avatar>
);

const TeamDetailPage = () => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();

  const { data: teams = [], isLoading } = useGetTeamsQuery();
  const [updateTeam, { isLoading: isUpdatingTeam }] = useUpdateTeamMutation();
  const [deleteTeam] = useDeleteTeamMutation();
  const [addTeamMembers, { isLoading: isAddingMembers }] = useAddTeamMembersMutation();
  const [updateTeamMember, { isLoading: isUpdatingMember }] = useUpdateTeamMemberMutation();
  const [removeTeamMember, { isLoading: isRemovingMember }] = useRemoveTeamMemberMutation();
  const isSaving = isUpdatingTeam || isAddingMembers || isUpdatingMember || isRemovingMember;

  const { data: teamShares } = useGetTeamSharesQuery(teamId ?? '', { skip: !teamId });
  const [updateFolderShare, { isLoading: isUpdatingShare }] = useUpdateFolderShareMutation();
  const [removeFolderShare, { isLoading: isRemovingShare }] = useRemoveFolderShareMutation();
  const isShareSaving = isUpdatingShare || isRemovingShare;

  const team = useMemo(() => teams.find((t) => t._id === teamId), [teams, teamId]);

  const { t } = useLocalization();

  const roleLabel = (role: TeamRole) => {
    const option = TEAM_ROLE_OPTIONS.find((o) => o.value === role);
    return option ? t(option.labelKey) : role;
  };
  const systemRoleLabel = (role: string) => t(`User_Role_${role.capitalize()}`);

  const { can, userId: currentUserId } = usePermission();
  const canManage = can('team:patch', team?._id);
  const canDelete = can('team:delete', team?._id);
  // Changing/removing your own membership needs global team authority; a
  // manager demoting themselves would lose their rights irreversibly.
  const canModifySelf = can('team:patch');
  const canManageShares = can('folder:share', team?._id);

  const shareLevelLabel = (level: ShareAccessLevel) => {
    const option = SHARE_LEVEL_OPTIONS.find((o) => o.value === level);
    return option ? t(option.labelKey) : level;
  };

  // Member names come populated with the team; the full user directory is only
  // needed (and only permitted) when managing members.
  const { data: users = [] } = useGetUsersQuery(undefined, { skip: !canManage });

  const [isEditing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [memberSearch, setMemberSearch] = useState('');

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const filteredMembers = useMemo(() => {
    if (!team) return [];
    const query = memberSearch.trim().toLowerCase();
    if (!query) return team.members;
    return team.members.filter((member) => {
      const user = resolveMemberUser(member);
      return user?.name.toLowerCase().includes(query) || user?.username.toLowerCase().includes(query);
    });
  }, [team, memberSearch]);

  const candidates = useMemo(() => {
    if (!team) return [];
    const memberIds = new Set(team.members.map(memberUserId));
    const query = candidateSearch.trim().toLowerCase();
    return users.filter((user) =>
      !memberIds.has(user.id) &&
      (!query || user.name.toLowerCase().includes(query) || user.username.toLowerCase().includes(query))
    );
  }, [team, users, candidateSearch]);

  const managerCount = team?.members.filter((member) => member.role === 'MANAGER').length ?? 0;

  const givenShares = teamShares?.given ?? [];
  const receivedShares = teamShares?.received ?? [];

  const startEditing = () => {
    if (!team) return;
    setEditName(team.name);
    setEditDescription(team.description);
    setEditing(true);
  };

  const handleSaveTeam = async () => {
    if (!team || editName.trim().length < 2 || editDescription.trim().length < 2) return;
    const result = await updateTeam({
      id: team._id,
      name: editName.trim(),
      description: editDescription.trim(),
    });
    if (!result.error) setEditing(false);
  };

  const handleDeleteTeam = async () => {
    if (!team) return;
    const result = await deleteTeam(team._id);
    if (!result.error) navigate(ROUTE_PATHS.TEAMS);
  };

  const handleChangeRole = (userId: string, role: TeamRole) => {
    if (!team) return;
    void updateTeamMember({ id: team._id, userId, role });
  };

  const handleRemoveMember = (userId: string) => {
    if (!team) return;
    void removeTeamMember({ id: team._id, userId });
  };

  const closeAddDialog = () => {
    if (isSaving) return;
    setAddOpen(false);
    setCandidateSearch('');
    setSelectedIds([]);
  };

  const toggleCandidate = (userId: string) => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]);
  };

  const handleAddMembers = async () => {
    if (!team || selectedIds.length === 0) return;
    const members: TeamMemberInput[] = selectedIds.map((userId) => ({ userId, role: 'INSPECTOR' }));
    const result = await addTeamMembers({ id: team._id, members });
    if (!result.error) closeAddDialog();
  };

  const renderShareRow = (share: TeamShareEntry, direction: 'given' | 'received') => {
    const isGiven = direction === 'given';
    const rowActionsDisabled = isShareSaving || (isGiven ? !canManageShares || !share.team : !canManage);
    const rowTooltip = isGiven
      ? (canManageShares ? t('Folder_Share_Revoke') : t('Permission_NotAllowed'))
      : (canManage ? t('Team_Shares_Leave') : t('Permission_NotAllowed'));

    const handleRemoveShare = () => {
      if (!team) return;
      const targetTeamId = isGiven ? share.team?._id : team._id;
      if (targetTeamId) void removeFolderShare({ folderId: share.folder._id, teamId: targetTeamId });
    };

    return (
      <Stack
        key={`${direction}-${share.folder._id}-${share.team?._id ?? 'unknown'}`}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={memberRowStyles}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography noWrap sx={{ fontWeight: 600, fontSize: 14.5 }}>{share.folder.name}</Typography>
            {share.team && <Chip size="small" label={share.team.name} sx={roleChipStyles} />}
          </Stack>
          <Typography noWrap color="text.secondary" sx={{ fontSize: 13 }}>{share.folder.path}</Typography>
        </Box>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {isGiven ? (
            <FormControl size="small">
              <Select<ShareAccessLevel>
                value={share.accessLevel}
                onChange={(e) => {
                  if (share.team) {
                    void updateFolderShare({
                      folderId: share.folder._id,
                      teamId: share.team._id,
                      accessLevel: e.target.value as ShareAccessLevel,
                    });
                  }
                }}
                renderValue={(value) => shareLevelLabel(value)}
                disabled={rowActionsDisabled}
                sx={selectStyles}
              >
                {SHARE_LEVEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value} sx={menuItemStyles}>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{t(option.labelKey)}</Typography>
                      <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4 }}>
                        {t(option.descriptionKey)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Chip size="small" label={shareLevelLabel(share.accessLevel)} sx={roleChipStyles} />
          )}
          <Tooltip title={rowTooltip} arrow>
            <span>
              <IconButton
                size="small"
                onClick={handleRemoveShare}
                sx={dangerHoverStyles}
                disabled={rowActionsDisabled}
              >
                <TrashIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (!team) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <Button onClick={() => navigate(ROUTE_PATHS.TEAMS)} startIcon={<AngleLeftIcon />} color="inherit" sx={backButtonStyles}>
          {t('Team_BackToTeams')}
        </Button>
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {t('Team_Detail_NotFound')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={detailIconStyles}>
              <UserFriendsIcon />
            </Box>

            {isEditing ? (
              <Stack spacing={2} sx={{ flexGrow: 1 }}>
                <TextField
                  size="small"
                  label={t('Team_Name')}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  sx={{ maxWidth: 420 }}
                  fullWidth
                  disabled={isSaving}
                />
                <TextField
                  size="small"
                  label={t('Common_Description')}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                  disabled={isSaving}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    size="small"
                    disableElevation
                    onClick={handleSaveTeam}
                    sx={buttonStyles}
                    disabled={isSaving || editName.trim().length < 2 || editDescription.trim().length < 2}
                  >
                    {t('Team_SaveChanges')}
                  </Button>
                  <Button color="inherit" size="small" onClick={() => setEditing(false)} sx={buttonStyles} disabled={isSaving}>
                    {t('Common_Cancel')}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>{team.name}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                    {team.description}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {t('Team_Member_Count', { count: team.members.length })}
                    {managerCount > 0 && ` · ${t('Team_Manager_Count', { count: managerCount })}`}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <Tooltip title={canManage ? t('Team_Edit') : t('Permission_NotAllowed')} arrow>
                    <span>
                      <IconButton onClick={startEditing} sx={{ color: 'text.secondary' }} disabled={!canManage}>
                        <EditIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={canDelete ? t('Team_Delete') : t('Permission_NotAllowed')} arrow>
                    <span>
                      <IconButton onClick={() => setDeleteOpen(true)} sx={dangerHoverStyles} disabled={!canDelete}>
                        <TrashIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </>
            )}
          </Stack>
        </Paper>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Team_Members')}</Typography>
            <Box component="span" sx={countPillStyles}>{team.members.length}</Box>
          </Stack>
          {canManage && (
            <Button
              variant="contained"
              disableElevation
              startIcon={<UserPlusIcon style={{ fontSize: 18 }} />}
              onClick={() => setAddOpen(true)}
              sx={buttonStyles}
            >
              {t('Team_AddUser')}
            </Button>
          )}
        </Stack>

        <TextField
          fullWidth
          size="small"
          placeholder={t('Team_Member_Search')}
          value={memberSearch}
          onChange={(e) => setMemberSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ opacity: 0.6 }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2 }}
        />

        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {filteredMembers.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {t('Team_NoMembers')}
            </Typography>
          )}

          {filteredMembers.map((member) => {
            const user = resolveMemberUser(member);
            const userId = memberUserId(member) ?? '';
            const displayName = user?.name ?? t('Team_UnknownUser');
            const isSelfLocked = userId === currentUserId && !canModifySelf;
            const memberActionsDisabled = isSaving || !canManage || isSelfLocked;
            const memberLockTooltip = !canManage
              ? t('Permission_NotAllowed')
              : isSelfLocked ? t('Team_CannotModifySelf') : '';

            return (
              <Stack
                key={userId}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={memberRowStyles}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
                  <MemberAvatar name={displayName} size={38} />
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography noWrap sx={{ fontWeight: 600, fontSize: 14.5 }}>{displayName}</Typography>
                      {user && (
                        <Tooltip title={t('Team_SystemRole')} arrow>
                          <Chip size="small" label={systemRoleLabel(user.role)} sx={roleChipStyles} />
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography noWrap color="text.secondary" sx={{ fontSize: 13 }}>
                      {user ? `@${user.username}` : userId}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
                  <Tooltip title={memberLockTooltip} arrow>
                    <FormControl size="small">
                      <Select<TeamRole>
                        value={member.role}
                        onChange={(e) => handleChangeRole(userId, e.target.value as TeamRole)}
                        renderValue={(value) => roleLabel(value)}
                        disabled={memberActionsDisabled}
                        sx={selectStyles}
                      >
                        {TEAM_ROLE_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value} sx={menuItemStyles}>
                            <Box>
                              <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{t(option.labelKey)}</Typography>
                              <Typography color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.4 }}>
                                {t(option.descriptionKey)}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Tooltip>
                  <Tooltip title={memberLockTooltip || t('Team_RemoveMember')} arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveMember(userId)}
                        sx={dangerHoverStyles}
                        disabled={memberActionsDisabled}
                      >
                        <TrashIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            );
          })}
        </Paper>

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mt: 3, mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('Team_Shares_Title')}</Typography>
          <Box component="span" sx={countPillStyles}>{givenShares.length + receivedShares.length}</Box>
        </Stack>

        <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
          {givenShares.length === 0 && receivedShares.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              {t('Team_Shares_Empty')}
            </Typography>
          )}

          {givenShares.length > 0 && (
            <>
              <Typography sx={shareSectionHeaderStyles}>{t('Team_Shares_Given')}</Typography>
              {givenShares.map((share) => renderShareRow(share, 'given'))}
            </>
          )}

          {receivedShares.length > 0 && (
            <>
              <Typography sx={shareSectionHeaderStyles}>{t('Team_Shares_Received')}</Typography>
              {receivedShares.map((share) => renderShareRow(share, 'received'))}
            </>
          )}
        </Paper>
      </Box>

      <Dialog
        open={canManage && addOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: { sx: { ...dialogPaperStyles, maxWidth: 530 } } }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={dialogHeaderStyles}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 16, lineHeight: 1.4 }}>{t('Team_AddMembers_Title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {t('Team_AddMembers_Subtitle')}
            </Typography>
          </Box>
          <IconButton size="small" onClick={closeAddDialog} sx={dialogCloseStyles}>
            <TimesIcon />
          </IconButton>
        </Stack>

        <Box sx={{ px: 2.5, pb: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={t('Team_AddMembers_Search')}
            value={candidateSearch}
            onChange={(e) => setCandidateSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ opacity: 0.5, fontSize: 18 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>

        <Divider />

        <Box sx={candidateListStyles}>
          {candidates.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center', fontSize: 14 }}>
              {t('Team_AddMembers_NoUsers')}
            </Typography>
          )}

          {candidates.map((user) => {
            const selected = selectedIds.includes(user.id);

            return (
              <Stack
                key={user.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={selected ? [candidateRowStyles, candidateSelectedStyles] : candidateRowStyles}
                onClick={() => toggleCandidate(user.id)}
              >
                <MemberAvatar name={user.name} size={34} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography noWrap sx={{ fontWeight: 600, fontSize: 14 }}>{user.name}</Typography>
                    <Chip size="small" label={systemRoleLabel(user.role)} sx={roleChipStyles} />
                  </Stack>
                  <Typography noWrap color="text.secondary" sx={{ fontSize: 13 }}>
                    @{user.username}
                  </Typography>
                </Box>
                <Box sx={selected ? [checkCircleStyles, checkCircleSelectedStyles] : checkCircleStyles}>
                  {selected && <CheckIcon sx={{ fontSize: 14 }} />}
                </Box>
              </Stack>
            );
          })}
        </Box>

        <Divider />

        <Stack direction="row" alignItems="center" spacing={1} sx={dialogFooterStyles}>
          <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t('Team_AddMembers_SelectedCount', [selectedIds.length])}
          </Typography>
          <Button color="inherit" size="small" onClick={closeAddDialog} sx={buttonStyles} disabled={isSaving}>
            {t('Common_Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={handleAddMembers}
            sx={buttonStyles}
            disabled={isSaving || selectedIds.length === 0}
          >
            {t('Team_AddMembers_Submit', { count: selectedIds.length })}
          </Button>
        </Stack>
      </Dialog>

      <DeleteConfirmationDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteTeam}
      />
    </Box>
  );
};

const shareSectionHeaderStyles = {
  px: 2,
  pt: 1.5,
  pb: 0.5,
  fontSize: 12.5,
  fontWeight: 600,
  color: 'text.secondary',
  textTransform: 'uppercase' as const,
  letterSpacing: 0.4,
};

export default TeamDetailPage;
